import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { PersonaAiBadge } from '../PersonaAiBadge';
import { isAiGeneratedPersona } from '../../lib/personaSource';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useAudioPlayback } from '../../hooks/useAudioPlayback';
import { useWebcam } from '../../hooks/useWebcam';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useSwipe } from '../../hooks/useSwipe';
import { useAuthContext } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { FLUID_SILENCE_MS_DEFAULT, parseFluidSilenceMsFromApi } from '../../lib/fluidSilenceMs';
import { ensureAudioUnlocked } from '../../lib/mobileAudioUnlock';
import { isMobileBrowser } from '../../lib/isMobileBrowser';
import type {
  Evaluation,
  Message,
  Participant,
  SessionInfo,
  SessionUsage,
  SessionViewProps,
} from './types';
import { getPersonaColor } from './types';
import { formatTimer } from './formatTimer';
import { getTileRows, getTileStyle, TILE_GAP_PX } from './tileGridLayout';
import { PracticeSessionReviewHeader } from './PracticeSessionReviewHeader';
import { SessionEvaluationCard } from './SessionEvaluationCard';
import { RecordingPrivateNotice } from './RecordingPrivateNotice';
import { SessionTranscriptPanel } from './SessionTranscriptPanel';
import { TranscriptToggleButton } from './TranscriptToggleButton';
import { getInitialShowTranscript } from './transcriptVisibility';
import { assignPersonaFaceIds, personaFacePublicPath } from '../../lib/personaFaceAssignment';
import { practiceModeLabel } from '../../lib/practiceModeLabel';
import { TavusVideoTile } from './TavusVideoTile';
import { LoadingQuotesDialog } from '../LoadingQuotesDialog';

function getInitials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function PersonaTileAvatar({
  name,
  faceUrl,
  showImage,
}: {
  name: string;
  faceUrl: string | null;
  showImage: boolean;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    setImgFailed(false);
  }, [faceUrl, showImage]);

  if (!showImage || !faceUrl || imgFailed) {
    return (
      <span className="text-4xl sm:text-6xl font-bold text-gray-300">{getInitials(name)}</span>
    );
  }
  return (
    <img
      src={faceUrl}
      alt={`${name} (demo participant)`}
      className="w-full h-full object-cover"
      onError={() => setImgFailed(true)}
    />
  );
}

type SpeechMode = 'fluid' | 'turn-by-turn';

function VerbalCompletedSessionReview({
  sessionInfo,
  messages,
  evaluation,
  participants,
  sessionUsage,
  showCost,
  showTranscript,
  onToggleTranscript,
  onBack,
  isOwnSession,
  user,
}: {
  sessionInfo: SessionInfo;
  messages: Message[];
  evaluation: Evaluation | null;
  participants: Participant[];
  sessionUsage: SessionUsage | null | undefined;
  showCost: boolean;
  showTranscript: boolean;
  onToggleTranscript: () => void;
  onBack: () => void;
  isOwnSession: boolean;
  user: { id?: string; name?: string | null } | null;
}) {
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const recordingHidden = sessionInfo.access === 'summary';

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] md:h-[calc(100vh-8rem)]">
      <PracticeSessionReviewHeader
        sessionInfo={sessionInfo}
        sessionUsage={sessionUsage}
        participants={participants}
        isOwnSession={isOwnSession}
        modeLabel={practiceModeLabel('verbal')}
        showCost={showCost}
      >
        {!recordingHidden && (
          <TranscriptToggleButton showTranscript={showTranscript} onToggle={onToggleTranscript} />
        )}
        <button
          type="button"
          onClick={onBack}
          className="text-xs sm:text-sm text-gray-600 hover:text-gray-900"
        >
          <span className="hidden sm:inline">Back to List</span>
          <span className="sm:hidden">Back</span>
        </button>
      </PracticeSessionReviewHeader>

      <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col gap-4 min-h-0 overflow-y-auto">
          {evaluation ? (
            <SessionEvaluationCard
              evaluation={evaluation}
              goalLabel={sessionInfo.goalLabel}
              feedbackHidden={recordingHidden}
            />
          ) : recordingHidden ? (
            <RecordingPrivateNotice />
          ) : null}
        </div>

        {showTranscript && !recordingHidden ? (
          <SessionTranscriptPanel
            headingId="verbal-session-transcript-heading"
            messages={messages}
            sessionInfo={sessionInfo}
            participants={participants}
            user={user}
            messagesEndRef={transcriptEndRef}
            showMobileHideButton
            onHideTranscript={onToggleTranscript}
          />
        ) : null}
      </div>
    </div>
  );
}

export function VerbalSessionView({
  sessionInfo,
  messages,
  evaluation,
  loading,
  isCompleted,
  isOwnSession,
  remainingSeconds,
  ending,
  onSendMessage,
  onEndSession,
  onBack,
  sessionUsage,
  participants = [],
  speakingUsers = new Set(),
  postSpeaking,
  showTranscriptByDefault = false,
  showPersonaFaces = true,
}: SessionViewProps) {
  const { user } = useAuthContext();
  const showCost = false;
  const [showTranscript, setShowTranscript] = useState(() =>
    getInitialShowTranscript(showTranscriptByDefault ?? false),
  );

  useEffect(() => {
    setShowTranscript(getInitialShowTranscript(showTranscriptByDefault ?? false));
  }, [sessionInfo.id, showTranscriptByDefault]);

  const [initialized, setInitialized] = useState(false);
  const [tavusVideoReady, setTavusVideoReady] = useState(false);
  /** Avoid auto-starting mic in "fluid" before we know platform speech_mode (default state is misleading). */
  const [speechConfigLoaded, setSpeechConfigLoaded] = useState(false);
  const [speechMode, setSpeechMode] = useState<SpeechMode>('fluid');
  const [fluidSilenceMs, setFluidSilenceMs] = useState(FLUID_SILENCE_MS_DEFAULT);
  const [ttsProvider, setTtsProvider] = useState<'browser' | 'openai'>('browser');
  const [showDeviceSetup, setShowDeviceSetup] = useState(false);
  const [pendingAudioInputId, setPendingAudioInputId] = useState<string | null>(null);
  const [pendingVideoInputId, setPendingVideoInputId] = useState<string | null>(null);
  const [isApplyingDeviceSetup, setIsApplyingDeviceSetup] = useState(false);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const liveCaptionRef = useRef<HTMLParagraphElement>(null);
  const pendingSendRef = useRef(false);
  const prevIsPlayingRef = useRef(false);
  const tileAreaRef = useRef<HTMLDivElement | null>(null);
  const [tileAreaSize, setTileAreaSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const isMobile = useMemo(() => isMobileBrowser(), []);

  const isDesktop = useMediaQuery('(min-width: 768px)');
  const swipeToTranscript = useCallback(() => {
    if (!isDesktop) setShowTranscript(true);
  }, [isDesktop]);
  const swipeToVideo = useCallback(() => {
    if (!isDesktop) setShowTranscript(false);
  }, [isDesktop]);
  const videoSwipe = useSwipe(swipeToTranscript, () => {});
  const transcriptSwipe = useSwipe(() => {}, swipeToVideo);

  useEffect(() => {
    const node = tileAreaRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    setTileAreaSize({ w: rect.width, h: rect.height });
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setTileAreaSize((prev) =>
          Math.abs(prev.w - width) < 1 && Math.abs(prev.h - height) < 1
            ? prev
            : { w: width, h: height },
        );
      }
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [initialized]);

  const isFluid = speechMode === 'fluid';

  const handleSilence = useCallback(
    (text: string) => {
      if (pendingSendRef.current) return;
      pendingSendRef.current = true;
      onSendMessage(text).finally(() => {
        pendingSendRef.current = false;
      });
    },
    [onSendMessage],
  );

  const { transcript, interimTranscript, isListening, isSupported, start, stop, resetTranscript } =
    useSpeechRecognition(
      isFluid
        ? { silenceTimeoutMs: fluidSilenceMs, onSilence: handleSilence, continuous: true }
        : {},
    );

  const { isPlaying, play, stop: stopAudio } = useAudioPlayback(ttsProvider);
  const [tavusConversationUrl, setTavusConversationUrl] = useState<string | null>(null);
  // On mobile, skip getUserMedia audio to avoid conflicts with SpeechRecognition.
  const webcamOpts = useMemo(() => ({ requestAudio: !isMobile }), [isMobile]);
  const {
    videoRef,
    isCameraOn,
    isMicOn,
    isPermissionDenied,
    audioInputDevices,
    videoInputDevices,
    selectedAudioInputId,
    selectedVideoInputId,
    micLevel,
    deviceError,
    toggleCamera,
    toggleMic,
    init,
    refreshDevices,
    applyDeviceSelection,
    cleanup,
  } = useWebcam(webcamOpts);

  useEffect(() => {
    api
      .get('/practice/tts/config')
      .then((res) => {
        const mode = res.data.speechMode;
        if (mode === 'turn-by-turn' || mode === 'fluid') {
          setSpeechMode(mode);
        }
        const p = res.data.provider;
        if (p === 'openai' || p === 'browser') {
          setTtsProvider(p);
        }
        setFluidSilenceMs(parseFluidSilenceMsFromApi(res.data.fluidSilenceMs));
      })
      .catch(() => {})
      .finally(() => setSpeechConfigLoaded(true));
  }, []);

  useEffect(() => {
    if (!isCompleted && (isOwnSession || sessionInfo.isParticipant)) {
      init().then(() => {
        setInitialized(true);
      });
    } else {
      setInitialized(true);
    }
    return cleanup;
  }, [init, cleanup, isCompleted, isOwnSession, sessionInfo.isParticipant]);

  const canParticipate = isOwnSession || sessionInfo.isParticipant;

  const personaListForTiles = useMemo(() => {
    if (sessionInfo.personas && sessionInfo.personas.length > 0) {
      return sessionInfo.personas;
    }
    return [
      {
        id: '__primary',
        name: sessionInfo.personaName,
        jobTitle: sessionInfo.personaJobTitle ?? '',
        type: '',
        source: sessionInfo.personaSource,
        isPrimary: true,
      },
    ];
  }, [sessionInfo]);

  const faceIdByPersonaId = useMemo(() => {
    if (!showPersonaFaces) return new Map<string, string>();
    return assignPersonaFaceIds({
      sessionId: sessionInfo.id,
      personas: personaListForTiles.map((p) => ({
        id: p.id,
        name: p.name,
        voice: p.voice ?? null,
        gender: ('gender' in p ? p.gender : null) ?? null,
      })),
    });
  }, [showPersonaFaces, sessionInfo.id, personaListForTiles]);

  useEffect(() => {
    if (
      sessionInfo.mode !== 'verbal' ||
      isCompleted ||
      !sessionInfo.tavusEnabled ||
      !sessionInfo.tavusConversationActive
    ) {
      setTavusConversationUrl(null);
      return;
    }
    let cancelled = false;
    void api
      .get<{ conversationUrl: string }>(`/practice/sessions/${sessionInfo.id}/tavus`)
      .then((res) => {
        if (!cancelled) {
          console.debug('[Tavus] fetched conversation URL', { sessionId: sessionInfo.id });
          setTavusConversationUrl(res.data.conversationUrl);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[Tavus] failed to fetch conversation URL', {
            sessionId: sessionInfo.id,
            status: err?.response?.status,
          });
          setTavusConversationUrl(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    sessionInfo.id,
    sessionInfo.mode,
    isCompleted,
    sessionInfo.tavusEnabled,
    sessionInfo.tavusConversationActive,
  ]);

  // Fluid mode: start open-mic only after we know platform mode (not default-before-fetch).
  useEffect(() => {
    if (
      speechConfigLoaded &&
      isFluid &&
      initialized &&
      !isCompleted &&
      canParticipate &&
      isSupported
    ) {
      start();
    }
  }, [speechConfigLoaded, isFluid, initialized, isCompleted, canParticipate, isSupported, start]);

  // Broadcast speaking state to other participants
  useEffect(() => {
    if (!postSpeaking) return;
    postSpeaking(isListening);
  }, [isListening, postSpeaking]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-scroll live caption to bottom as new text arrives
  useEffect(() => {
    const el = liveCaptionRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript, interimTranscript]);

  useEffect(() => {
    setPendingAudioInputId(selectedAudioInputId);
  }, [selectedAudioInputId]);

  useEffect(() => {
    setPendingVideoInputId(selectedVideoInputId);
  }, [selectedVideoInputId]);

  // Pause recognition while TTS plays to prevent speaker audio from being
  // picked up by the mic (echo feedback). Resume once playback ends.
  useEffect(() => {
    if (!isFluid) return;
    if (isPlaying) {
      if (isListening) stop();
    } else if (prevIsPlayingRef.current && !isCompleted && canParticipate) {
      if (!isListening && isSupported) {
        start();
      }
    }
    prevIsPlayingRef.current = isPlaying;
  }, [isFluid, isPlaying, isListening, isCompleted, canParticipate, isSupported, start, stop]);

  // Turn-by-turn: push-to-talk mic toggle
  const handleMicToggle = useCallback(() => {
    if (isListening) {
      stop();
    } else {
      resetTranscript();
      start();
    }
  }, [isListening, start, stop, resetTranscript]);

  // Turn-by-turn: auto-send when recording stops and there is transcript
  useEffect(() => {
    if (!isFluid && !isListening && transcript && !pendingSendRef.current && !loading) {
      pendingSendRef.current = true;
      const text = transcript;
      resetTranscript();
      onSendMessage(text).finally(() => {
        pendingSendRef.current = false;
      });
    }
  }, [isFluid, isListening, transcript, loading, resetTranscript, onSendMessage]);

  // Play TTS for latest assistant message
  useEffect(() => {
    if (messages.length > 0) {
      const last = messages[messages.length - 1];
      if (last.role === 'assistant' && !isCompleted) {
        const skipTts =
          Boolean(sessionInfo.tavusEnabled && sessionInfo.tavusConversationActive) &&
          Boolean(tavusConversationUrl) &&
          Boolean(sessionInfo.tavusVideoPersonaId) &&
          last.personaId === sessionInfo.tavusVideoPersonaId;
        if (skipTts) {
          stopAudio();
        } else {
          play(last.content, sessionInfo.id, last.personaId ?? undefined);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    messages.length,
    tavusConversationUrl,
    sessionInfo.id,
    sessionInfo.tavusEnabled,
    sessionInfo.tavusConversationActive,
    sessionInfo.tavusVideoPersonaId,
    isCompleted,
  ]);

  // Turn-by-turn: spacebar push-to-talk
  useEffect(() => {
    if (isFluid) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.code === 'Space' &&
        !e.repeat &&
        e.target === document.body &&
        !isCompleted &&
        canParticipate
      ) {
        e.preventDefault();
        if (!isListening) {
          resetTranscript();
          start();
        }
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && e.target === document.body) {
        e.preventDefault();
        if (isListening) stop();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isFluid, isListening, start, stop, resetTranscript, isCompleted, canParticipate]);

  // Fluid mode: mute toggles recognition + media track
  const handleFluidMuteToggle = useCallback(() => {
    if (isListening) {
      stop();
      toggleMic();
    } else {
      toggleMic();
      resetTranscript();
      start();
    }
  }, [isListening, start, stop, resetTranscript, toggleMic]);

  const openDeviceSetup = useCallback(() => {
    setShowDeviceSetup(true);
    void refreshDevices();
  }, [refreshDevices]);

  const handleApplyDeviceSetup = useCallback(async () => {
    setIsApplyingDeviceSetup(true);
    await applyDeviceSelection(pendingAudioInputId, pendingVideoInputId);
    setIsApplyingDeviceSetup(false);
  }, [applyDeviceSelection, pendingAudioInputId, pendingVideoInputId]);

  // Unlock Web Audio on first user interaction so programmatic TTS playback
  // works on mobile even outside a direct gesture handler.
  const audioUnlockedRef = useRef(false);
  const handleUnlockAudio = useCallback(() => {
    if (audioUnlockedRef.current) return;
    audioUnlockedRef.current = true;
    ensureAudioUnlocked();
  }, []);

  const handleTavusVideoReady = useCallback(() => setTavusVideoReady(true), []);

  const isTavusSession =
    Boolean(sessionInfo.tavusEnabled) && Boolean(sessionInfo.tavusConversationActive);

  if (!initialized) {
    if (isTavusSession) {
      return (
        <div className="relative h-[calc(100vh-8rem)] bg-gray-900 rounded-lg overflow-hidden">
          <LoadingQuotesDialog
            open
            variant="fullscreen"
            title="Starting your video session"
            subtitle="Your AI sales persona is warming up…"
          />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)] bg-gray-900 rounded-lg">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-300 text-lg">Connecting...</p>
          <p className="text-gray-500 text-sm mt-1">Setting up camera and microphone</p>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <VerbalCompletedSessionReview
        sessionInfo={sessionInfo}
        messages={messages}
        evaluation={evaluation}
        participants={participants}
        sessionUsage={sessionUsage}
        showCost={showCost}
        showTranscript={showTranscript}
        onToggleTranscript={() => setShowTranscript((v) => !v)}
        onBack={onBack}
        isOwnSession={isOwnSession}
        user={user}
      />
    );
  }

  return (
    <div
      className="flex flex-col md:flex-row h-[calc(100vh-7rem)] md:h-[calc(100vh-8rem)] gap-0"
      onClickCapture={handleUnlockAudio}
      onTouchStartCapture={handleUnlockAudio}
    >
      {/* Main meeting area — hidden on mobile when transcript tab is active */}
      <div
        className={`relative flex-1 flex-col bg-gray-900 rounded-lg overflow-hidden min-h-0 ${showTranscript && !isDesktop ? 'hidden' : 'flex'} md:flex`}
        {...videoSwipe}
      >
        {/* Tavus startup overlay — shown until the remote video track is live */}
        {isTavusSession && !tavusVideoReady && (
          <LoadingQuotesDialog
            open
            variant="fullscreen"
            title="Starting your video session"
            subtitle="Your AI sales persona is warming up…"
          />
        )}
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-white text-sm font-semibold">
              {sessionInfo.personas && sessionInfo.personas.length > 1
                ? `Committee (${sessionInfo.personas.length})`
                : sessionInfo.personaName}
            </h1>
            {isAiGeneratedPersona(sessionInfo.personaSource) && (
              <PersonaAiBadge variant="dark" className="scale-90 origin-left" />
            )}
            {sessionInfo.goalLabel && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-900/50 text-blue-300">
                Goal: {sessionInfo.goalLabel}
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
              {sessionInfo.pitchStyle}
            </span>
            {sessionUsage && (sessionUsage.inputTokens > 0 || sessionUsage.outputTokens > 0) && (
              <span className="text-xs text-gray-400">
                {sessionUsage.inputTokens + sessionUsage.outputTokens} tok
                {showCost && sessionUsage.costUsd !== undefined && (
                  <span> · ~${sessionUsage.costUsd.toFixed(2)}</span>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3" data-tour="ps-verbal-top-meta">
            {remainingSeconds != null && !isCompleted && (
              <span
                className={`tabular-nums text-sm font-semibold px-3 py-1 rounded-md ${
                  remainingSeconds <= 0
                    ? 'bg-red-800/70 text-red-200 animate-pulse'
                    : remainingSeconds <= 60
                      ? 'bg-red-900/50 text-red-300 animate-pulse'
                      : remainingSeconds <= 300
                        ? 'bg-amber-900/50 text-amber-300'
                        : 'bg-gray-700 text-gray-300'
                }`}
              >
                {formatTimer(remainingSeconds)}
              </span>
            )}
            {participants.length > 1 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-900/50 text-indigo-300">
                {participants.length} participants
              </span>
            )}
            {!isOwnSession && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-900/50 text-purple-300">
                {sessionInfo.userName || sessionInfo.userEmail}
              </span>
            )}
          </div>
        </div>

        {speechConfigLoaded && !isSupported && !isCompleted && isOwnSession && (
          <div className="px-4 py-2 bg-red-900/50 border-b border-red-800 text-red-100 text-sm text-center">
            Speech recognition is not available in this browser. Use Chrome or Edge on desktop for
            video call practice, or start a chat practice session.
          </div>
        )}

        {speechConfigLoaded && isSupported && !isFluid && !isCompleted && isOwnSession && (
          <div className="px-4 py-2 bg-amber-900/40 border-b border-amber-800/50 text-amber-100 text-sm text-center">
            <span className="font-medium">Push-to-talk:</span> click the microphone button or hold
            the Space bar (with this page focused), speak, then release to send. The mic is not
            always listening in this mode.
          </div>
        )}

        {/* Participant tiles — aspect-aware square-tile layout.
            Each tile is sized to the largest square that fits the meeting
            area for the chosen `cols × rows`. 2 tiles side-by-side on wide
            desktops and stacked on tall mobile; partial last rows (e.g.
            3 tiles = 2 top + 1 centered) are centered via flex. */}
        {(() => {
          const personaList = personaListForTiles;
          const remoteParticipants = participants.filter((p) => p.userId !== user?.id);
          const totalTiles = personaList.length + 1 + remoteParticipants.length;
          const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');
          const rows = getTileRows(totalTiles, tileAreaSize.w, tileAreaSize.h);
          const tileStyle = getTileStyle(totalTiles, tileAreaSize.w, tileAreaSize.h);

          const renderTile = (tileIndex: number) => {
            if (tileIndex < personaList.length) {
              const persona = personaList[tileIndex];
              const isSpeakingPersona = isPlaying && lastAssistantMsg?.personaId === persona.id;
              const color = getPersonaColor(tileIndex);
              const faceId = faceIdByPersonaId.get(persona.id);
              const faceUrl = faceId ? personaFacePublicPath(faceId) : null;
              const showTavusVideo =
                Boolean(tavusConversationUrl) &&
                Boolean(sessionInfo.tavusEnabled && sessionInfo.tavusConversationActive) &&
                Boolean(sessionInfo.tavusVideoPersonaId) &&
                persona.id === sessionInfo.tavusVideoPersonaId;
              return (
                <div key={persona.id} className="relative shrink-0" style={tileStyle}>
                  <div
                    className={`w-full h-full rounded-2xl bg-gray-700 flex items-center justify-center transition-all overflow-hidden ${
                      isSpeakingPersona ? `ring-4 ${color.ring} ring-opacity-75 animate-pulse` : ''
                    }`}
                  >
                    {showTavusVideo ? (
                      <TavusVideoTile
                        conversationUrl={tavusConversationUrl!}
                        className="w-full h-full"
                        onReady={handleTavusVideoReady}
                      />
                    ) : (
                      <PersonaTileAvatar
                        name={persona.name}
                        faceUrl={faceUrl}
                        showImage={showPersonaFaces}
                      />
                    )}
                  </div>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 px-2 py-1.5 flex items-end justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent rounded-b-2xl">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="text-white text-xs sm:text-sm font-medium truncate">
                          {persona.name}
                        </p>
                        {isAiGeneratedPersona(persona.source) && <PersonaAiBadge variant="dark" />}
                      </div>
                      {persona.jobTitle && (
                        <p className="text-gray-300 text-[10px] sm:text-xs truncate">
                          {persona.jobTitle}
                        </p>
                      )}
                    </div>
                    {isSpeakingPersona && (
                      <div className="flex gap-0.5 shrink-0">
                        <span
                          className="w-1 h-3 bg-green-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        />
                        <span
                          className="w-1 h-4 bg-green-400 rounded-full animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <span
                          className="w-1 h-2 bg-green-400 rounded-full animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            if (tileIndex === personaList.length) {
              return (
                <div key="__you" className="relative shrink-0" style={tileStyle}>
                  <div
                    className={`w-full h-full rounded-2xl overflow-hidden bg-gray-700 flex items-center justify-center transition-all ${
                      isListening ? 'ring-4 ring-green-400 ring-opacity-75' : ''
                    }`}
                  >
                    {!isPermissionDenied && isCameraOn ? (
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                        style={{ transform: 'scaleX(-1)' }}
                      />
                    ) : (
                      <span className="text-4xl sm:text-6xl font-bold text-gray-300">You</span>
                    )}
                  </div>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 px-2 py-1.5 flex items-end justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent rounded-b-2xl">
                    <p className="text-white text-xs sm:text-sm font-medium truncate">You</p>
                    {isListening && (
                      <div className="flex gap-0.5 shrink-0">
                        <span
                          className="w-1 h-3 bg-green-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        />
                        <span
                          className="w-1 h-4 bg-green-400 rounded-full animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <span
                          className="w-1 h-2 bg-green-400 rounded-full animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            const p = remoteParticipants[tileIndex - personaList.length - 1];
            const isSpeakingRemote = speakingUsers.has(p.userId);
            const displayName = p.userName || p.userEmail.split('@')[0];
            return (
              <div key={p.userId} className="relative shrink-0" style={tileStyle}>
                <div
                  className={`w-full h-full rounded-2xl bg-gray-700 flex items-center justify-center transition-all overflow-hidden ${
                    isSpeakingRemote ? 'ring-4 ring-green-400 ring-opacity-75' : ''
                  }`}
                >
                  <span className="text-4xl sm:text-6xl font-bold text-gray-300">
                    {getInitials(displayName)}
                  </span>
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 px-2 py-1.5 flex items-end justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent rounded-b-2xl">
                  <p className="text-white text-xs sm:text-sm font-medium truncate">
                    {displayName}
                  </p>
                  {isSpeakingRemote && (
                    <div className="flex gap-0.5 shrink-0">
                      <span
                        className="w-1 h-3 bg-green-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      />
                      <span
                        className="w-1 h-4 bg-green-400 rounded-full animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      />
                      <span
                        className="w-1 h-2 bg-green-400 rounded-full animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          };

          return (
            <div
              ref={tileAreaRef}
              data-testid="participant-tile-grid"
              data-tile-count={totalTiles}
              className="flex-1 flex flex-col items-center justify-center p-2 sm:p-4 min-h-0 min-w-0"
              style={{ rowGap: `${TILE_GAP_PX}px` }}
            >
              <p className="sr-only">
                AI-generated demo participant imagery when shown; not real people.
              </p>
              {rows.map((rowIndices, rowIdx) => (
                <div
                  key={rowIdx}
                  className="flex items-center justify-center"
                  style={{ columnGap: `${TILE_GAP_PX}px` }}
                >
                  {rowIndices.map(renderTile)}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Live caption bar — hidden on mobile to avoid layout disruption */}
        {(interimTranscript || (isListening && transcript)) && (
          <div className="hidden md:block px-6 pb-2">
            <div className="bg-black/60 rounded-lg px-4 py-2 text-center">
              <p
                ref={liveCaptionRef}
                className="text-white text-sm overflow-y-auto"
                style={{ maxHeight: '2.8em', lineHeight: '1.4' }}
              >
                {transcript}
                {interimTranscript && <span className="text-gray-400"> {interimTranscript}</span>}
              </p>
            </div>
          </div>
        )}

        {loading && (
          <div className="px-6 pb-2">
            <div className="bg-black/60 rounded-lg px-4 py-2 text-center">
              <p className="text-gray-400 text-sm animate-pulse">
                {sessionInfo.personas && sessionInfo.personas.length > 1
                  ? 'The committee is responding...'
                  : `${sessionInfo.personaName} is thinking...`}
              </p>
            </div>
          </div>
        )}

        {/* Mobile tab indicator — visible only on small screens */}
        {!isDesktop && (
          <div className="flex items-center justify-center gap-2 py-1.5 bg-gray-800/60 md:hidden">
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
            <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
          </div>
        )}

        {/* Bottom control bar */}
        <div
          className="flex items-center justify-center gap-2 sm:gap-4 py-3 sm:py-4 bg-gray-800"
          data-tour="ps-verbal-controls"
        >
          {!isCompleted && canParticipate && (
            <>
              {/* Turn-by-turn: push-to-talk button */}
              {!isFluid && (
                <button
                  onClick={handleMicToggle}
                  disabled={!isSupported || loading}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all disabled:opacity-30 ${
                    isListening
                      ? 'bg-green-500 text-white ring-4 ring-green-500/30'
                      : 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                  }`}
                  title={isListening ? 'Stop recording (Space)' : 'Start recording (Space)'}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
                    />
                    {!isListening && <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" />}
                  </svg>
                </button>
              )}

              {/* Mute/unmute */}
              <button
                onClick={isFluid ? handleFluidMuteToggle : toggleMic}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                  (isFluid ? isListening : isMicOn)
                    ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                    : 'bg-red-500 text-white'
                }`}
                title={
                  isFluid
                    ? isListening
                      ? 'Mute microphone'
                      : 'Unmute microphone'
                    : isMicOn
                      ? 'Mute microphone'
                      : 'Unmute microphone'
                }
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
                  />
                  {!(isFluid ? isListening : isMicOn) && (
                    <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" />
                  )}
                </svg>
              </button>

              {/* Camera toggle */}
              <button
                onClick={toggleCamera}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                  isCameraOn
                    ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                    : 'bg-red-500 text-white'
                }`}
                title={isCameraOn ? 'Turn off camera' : 'Turn on camera'}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  {isCameraOn ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  ) : (
                    <>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                      <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" />
                    </>
                  )}
                </svg>
              </button>

              {/* Device setup */}
              <button
                onClick={openDeviceSetup}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all bg-gray-600 text-gray-200 hover:bg-gray-500"
                title="Configure devices"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
                  />
                </svg>
              </button>
            </>
          )}

          {!isCompleted && isOwnSession && (
            <button
              type="button"
              data-tour="ps-verbal-end"
              onClick={() => {
                stopAudio();
                stop();
                onEndSession();
              }}
              disabled={ending}
              className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-colors disabled:opacity-50"
              title="End call"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z"
                />
              </svg>
            </button>
          )}

          <button
            onClick={() => setShowTranscript((v) => !v)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              showTranscript
                ? 'bg-blue-500 text-white'
                : 'bg-gray-600 text-gray-200 hover:bg-gray-500'
            }`}
            title="Toggle transcript"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Mobile: full-screen transcript tab ── */}
      {showTranscript && !isDesktop && (
        <div
          role="region"
          aria-label="Transcript"
          className="flex flex-1 flex-col bg-gray-900 rounded-lg overflow-hidden min-h-0 md:hidden"
          data-testid="mobile-transcript-tab"
          {...transcriptSwipe}
        >
          {/* Header */}
          <div className="shrink-0 px-4 py-3 bg-gray-800 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowTranscript(false)}
              className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Call
            </button>
            <h2 className="text-white text-sm font-semibold">Transcript</h2>
            <div className="w-20" />
          </div>

          {/* Tab indicator */}
          <div className="flex items-center justify-center gap-2 py-1.5 bg-gray-800/60">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
          </div>

          {/* Scrollable messages */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <p className="text-gray-500 text-sm text-center pt-8">
                Transcript messages will appear here as the conversation progresses.
              </p>
            )}
            {messages.map((msg) => {
              const personaIdx =
                msg.personaId && sessionInfo.personas
                  ? sessionInfo.personas.findIndex((p) => p.id === msg.personaId)
                  : 0;
              const speakerName =
                msg.role === 'user'
                  ? msg.userId === user?.id
                    ? 'You'
                    : msg.userName || 'Participant'
                  : msg.personaName || sessionInfo.personaName;
              const labelColors =
                msg.role === 'user'
                  ? 'text-green-400'
                  : (sessionInfo.personas?.length ?? 0) > 1
                    ? getPersonaColor(Math.max(0, personaIdx))
                        .label.replace('text-', 'text-')
                        .replace('-600', '-400')
                    : 'text-blue-400';

              return (
                <div key={msg.id}>
                  <span className={`text-xs font-semibold ${labelColors}`}>{speakerName}</span>
                  <p className="text-gray-300 text-sm mt-0.5 leading-relaxed">{msg.content}</p>
                  {msg.hint && (
                    <div className="mt-1 px-2 py-1 rounded bg-blue-900/30 border border-blue-800/50">
                      <p className="text-blue-300 text-xs">
                        <span className="font-medium">Tip:</span> {msg.hint}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={transcriptEndRef} />
          </div>

          {/* Compact call controls */}
          {!isCompleted && canParticipate && (
            <div className="shrink-0 flex items-center justify-center gap-3 py-3 bg-gray-800 border-t border-gray-700">
              {!isFluid && (
                <button
                  onClick={handleMicToggle}
                  disabled={!isSupported || loading}
                  className={`w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-30 ${
                    isListening
                      ? 'bg-green-500 text-white ring-4 ring-green-500/30'
                      : 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                  }`}
                  title={isListening ? 'Stop recording' : 'Start recording'}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
                    />
                    {!isListening && <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" />}
                  </svg>
                </button>
              )}
              <button
                onClick={isFluid ? handleFluidMuteToggle : toggleMic}
                className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                  (isFluid ? isListening : isMicOn)
                    ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                    : 'bg-red-500 text-white'
                }`}
                title={(isFluid ? isListening : isMicOn) ? 'Mute' : 'Unmute'}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
                  />
                  {!(isFluid ? isListening : isMicOn) && (
                    <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" />
                  )}
                </svg>
              </button>
              {isOwnSession && (
                <button
                  type="button"
                  onClick={() => {
                    stopAudio();
                    stop();
                    onEndSession();
                  }}
                  disabled={ending}
                  className="w-11 h-11 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-colors disabled:opacity-50"
                  title="End call"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Desktop: right sidebar transcript ── */}
      {showTranscript && isDesktop && (
        <div
          role="region"
          aria-label="Transcript"
          className="hidden md:flex md:static md:max-h-none md:h-full md:w-80 md:min-h-0 md:rounded-r-lg md:border-l md:border-gray-700 md:shadow-none flex-col bg-gray-800"
        >
          <div className="shrink-0 px-4 py-3 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-white text-sm font-semibold">Transcript</h2>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg) => {
              const personaIdx =
                msg.personaId && sessionInfo.personas
                  ? sessionInfo.personas.findIndex((p) => p.id === msg.personaId)
                  : 0;
              const speakerName =
                msg.role === 'user'
                  ? msg.userId === user?.id
                    ? 'You'
                    : msg.userName || 'Participant'
                  : msg.personaName || sessionInfo.personaName;
              const labelColors =
                msg.role === 'user'
                  ? 'text-green-400'
                  : (sessionInfo.personas?.length ?? 0) > 1
                    ? getPersonaColor(Math.max(0, personaIdx))
                        .label.replace('text-', 'text-')
                        .replace('-600', '-400')
                    : 'text-blue-400';

              return (
                <div key={msg.id}>
                  <div className="flex items-start gap-2">
                    <span className={`text-xs font-semibold shrink-0 ${labelColors}`}>
                      {speakerName}
                    </span>
                  </div>
                  <p className="text-gray-300 text-xs mt-0.5 leading-relaxed">{msg.content}</p>
                  {msg.hint && (
                    <div className="mt-1 px-2 py-1 rounded bg-blue-900/30 border border-blue-800/50">
                      <p className="text-blue-300 text-xs">
                        <span className="font-medium">Tip:</span> {msg.hint}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={transcriptEndRef} />
          </div>
        </div>
      )}

      {/* Evaluation modal overlay */}
      {showDeviceSetup && !isCompleted && isOwnSession && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-xl w-full p-6">
            <h2 className="text-lg font-semibold text-white">Device setup</h2>
            <p className="text-sm text-gray-300 mt-1">
              Select your microphone and camera, then verify your mic input before continuing.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Microphone</label>
                <select
                  value={pendingAudioInputId ?? ''}
                  onChange={(e) => setPendingAudioInputId(e.target.value || null)}
                  className="w-full rounded-md border border-gray-600 bg-gray-800 text-gray-100 px-3 py-2 text-sm"
                >
                  <option value="">Default microphone</option>
                  {audioInputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Camera</label>
                <select
                  value={pendingVideoInputId ?? ''}
                  onChange={(e) => setPendingVideoInputId(e.target.value || null)}
                  className="w-full rounded-md border border-gray-600 bg-gray-800 text-gray-100 px-3 py-2 text-sm"
                >
                  <option value="">Default camera</option>
                  {videoInputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-200">Microphone test</p>
                <div className="mt-2 h-3 w-full rounded-full bg-gray-700 overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all"
                    style={{ width: `${Math.max(4, Math.round(micLevel * 100))}%` }}
                  />
                </div>
                {isMicOn ? (
                  <p className="text-xs text-gray-300 mt-1">
                    {micLevel > 0.05
                      ? 'Input detected.'
                      : 'No input detected yet. Speak now or verify your microphone mute switch.'}
                  </p>
                ) : (
                  <p className="text-xs text-amber-300 mt-1">
                    Your mic is muted in call controls. Unmute to test input.
                  </p>
                )}
              </div>

              {isPermissionDenied && (
                <div className="rounded-md bg-red-900/50 border border-red-800 px-3 py-2 text-sm text-red-200">
                  Camera or microphone permissions are denied. Allow access in your browser
                  settings.
                </div>
              )}
              {deviceError && (
                <div className="rounded-md bg-red-900/50 border border-red-800 px-3 py-2 text-sm text-red-200">
                  {deviceError}
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => void refreshDevices()}
                className="px-3 py-2 rounded-md text-sm bg-gray-700 text-gray-100 hover:bg-gray-600"
              >
                Refresh devices
              </button>
              <button
                onClick={() => setShowDeviceSetup(false)}
                className="px-3 py-2 rounded-md text-sm bg-gray-700 text-gray-100 hover:bg-gray-600"
              >
                Close
              </button>
              <button
                onClick={() => void handleApplyDeviceSetup()}
                disabled={isApplyingDeviceSetup}
                className="px-3 py-2 rounded-md text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                {isApplyingDeviceSetup ? 'Applying...' : 'Apply devices'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
